import type { AppStore } from '@/stores/app'
import {
  mergePageFieldMaps as mergeFieldMaps,
  type MergePageFieldMapsOptions,
} from '@/common/utils/mergePageFieldMaps'
import {
  collectClientDataNodes,
  collectMergedChangedFields,
  getRawChangedFields,
  isFieldUniDataTable,
} from '@/common/utils/jsonUniData.ts'

import {
  fieldHasRefillOrEmptyState,
  fieldEntryLooksEmptyIncoming,
  type FieldStatePreserveHint,
} from '@/common/utils/changedDataFieldMerge'
import {
  fieldParticipatesInTreeTableSearchCriteria,
  resolveTreeTableFieldNameFromPageFields,
} from '@/common/utils/treeTableSearch'
import {
  resetTreeTableSearchApplyState,
  setTreeTableSearchSubmittedCriteria,
} from '@/common/utils/treeTableSearchSubmitted'
import type { ActionContext, ActionDependencies } from '@/services/action/types'
import { mergePageDataFieldSnapshot } from '@/common/utils/resolveClientDataActualFields'
import { applyFormErrorFromPayload } from '@/common/utils/formErrorStore'
import { publishExchangeFieldAttributes } from '@/common/utils/fieldForeground'

export {
  fieldHasRefillOrEmptyState,
  shouldPreserveFieldEntryOnEmptyIncomingForStates,
} from '@/common/utils/changedDataFieldMerge'

export type { FieldStatePreserveHint }

function mergePageFieldsRespectingStates(
  states: FieldStatePreserveHint[] | undefined,
  mergeFieldMapsFn: (
    ...sources: Array<Record<string, any> | undefined | MergePageFieldMapsOptions>
  ) => Record<string, any>,
  ...sources: Array<Record<string, any> | undefined>
): Record<string, any> {
  if (!states?.length) {
    return mergeFieldMapsFn(...sources)
  }
  return mergeFieldMapsFn(
    {
      shouldPreserveEmptyIncoming: (key: string) =>
        !fieldHasRefillOrEmptyState(states, key),
    },
    ...sources,
  )
}

/** clientData.params из changed_data ответа middle (после reloadElement на сервере). */
export function extractClientDataParamsFromChangedData(
  changedData: unknown,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {}
  for (const clientData of collectClientDataNodes(changedData)) {
    const params = clientData.params
    if (!params || typeof params !== 'object' || Array.isArray(params)) continue
    Object.assign(merged, params as Record<string, unknown>)
  }
  return merged
}

export function applyForceReplaceTableFields(
  merged: Record<string, any>,
  changedFields: Record<string, any>,
  forceReplaceKeys: readonly string[],
): Record<string, any> {
  if (forceReplaceKeys.length === 0) return merged
  const next = { ...merged }
  for (const key of forceReplaceKeys) {
    if (Object.prototype.hasOwnProperty.call(changedFields, key)) {
      next[key] = changedFields[key]
    }
  }
  return next
}

/** Поля pagination таблицы в clientData.params — признак reloadElement/search. */
export function tablePaginationParamKeys(tableName: string): string[] {
  return [`${tableName}__page`, `${tableName}__maxOnPage`, `${tableName}__size`]
}

export function hasTablePaginationExchange(
  tableName: string,
  changedParams: Record<string, unknown>,
  changedFields?: Record<string, unknown>,
): boolean {
  if (
    tablePaginationParamKeys(tableName).some((key) =>
      Object.prototype.hasOwnProperty.call(changedParams, key),
    )
  ) {
    return true
  }
  if (!changedFields) return false
  return tablePaginationParamKeys(tableName).some((key) =>
    Object.prototype.hasOwnProperty.call(changedFields, key),
  )
}

function readStringFieldValue(field: unknown): string {
  if (field == null) return ''
  if (typeof field === 'string') return field.trim()
  if (typeof field === 'object' && field !== null && 'value' in field) {
    const v = (field as { value?: unknown }).value
    return v == null ? '' : String(v).trim()
  }
  return String(field).trim()
}

/** Непустой searchString в changed_data — reloadElement после Ctrl+F (317 и аналоги). */
export function hasActiveTableSearchInChangedFields(
  changedFields: Record<string, unknown>,
): boolean {
  return readStringFieldValue(changedFields.searchString).length > 0
}

function tableFieldRowCount(field: unknown): number {
  if (!isFieldUniDataTable(field)) return 0
  const raw =
    field && typeof field === 'object' && 'value' in field
      ? (field as { value?: unknown }).value
      : field
  const rows =
    raw && typeof raw === 'object' && raw !== null && 'rows' in raw
      ? (raw as { rows?: unknown }).rows
      : (field as { rows?: unknown })?.rows
  return Array.isArray(rows) ? rows.length : 0
}

/**
 * Полная замена таблицы только после reloadElement/поиска (SwingTable.reload)
 */
export function isAuthoritativeTableReloadField(
  tableName: string,
  fieldValue: unknown,
  changedParams: Record<string, unknown>,
  changedFields?: Record<string, unknown>,
): boolean {
  if (!isFieldUniDataTable(fieldValue)) return false
  if (tableFieldRowCount(fieldValue) > 0) return true
  if (hasTablePaginationExchange(tableName, changedParams, changedFields)) return true
  if (
    changedFields &&
    tableFieldRowCount(fieldValue) === 0 &&
    hasActiveTableSearchInChangedFields(changedFields)
  ) {
    return true
  }
  return false
}

/** @deprecated alias — используйте collectAuthoritativeTableReloadFieldNames */
export function collectExplicitUniDataTableFieldNames(
  changedFields: Record<string, unknown>,
): string[] {
  return Object.keys(changedFields).filter((key) => isFieldUniDataTable(changedFields[key]))
}

export function collectAuthoritativeTableReloadFieldNames(
  changedFields: Record<string, unknown>,
  changedParams: Record<string, unknown>,
): string[] {
  return Object.keys(changedFields).filter((key) =>
    isAuthoritativeTableReloadField(key, changedFields[key], changedParams, changedFields),
  )
}

export function syncTableFieldsToElementRegistry(
  changedFields: Record<string, any>,
  getElements?: () => Record<string, any>,
  authoritativeFieldNames?: readonly string[],
): void {
  if (typeof getElements !== 'function') return
  const elements = getElements()
  if (!elements || typeof elements !== 'object') return

  const authoritative =
    authoritativeFieldNames != null ? new Set(authoritativeFieldNames) : null

  for (const [fieldName, fieldValue] of Object.entries(changedFields)) {
    if (!isFieldUniDataTable(fieldValue)) continue
    if (authoritative != null && !authoritative.has(fieldName)) continue
    for (const el of Object.values(elements)) {
      if (el == null || typeof el !== 'object') continue
      const name = String(
        (el as { fieldName?: string; name?: string }).fieldName ??
          (el as { name?: string }).name ??
          '',
      ).trim()
      if (name !== fieldName) continue
      const registry = el as {
        registeredValue?: unknown
        value?: unknown
      }
      registry.registeredValue =
        fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue
          ? (fieldValue as { value: unknown }).value
          : fieldValue
      registry.value = fieldValue
    }
  }
}

export type ApplyChangedDataPagePatch = {
  uniFields: Record<string, any>
  jsResultFields: Record<string, any>
  clientDataParams?: Record<string, unknown>
  forceReplaceTableFields: string[]
}

/** Собирает patch для pagesData из sanitized changed_data (server onClick / reloadElement). */
export function buildChangedDataPagePatch(
  pageData: Record<string, any>,
  sanitizedChangedData: unknown,
  mergeFieldMapsFn: (...sources: Array<Record<string, any> | undefined>) => Record<string, any>,
  states?: FieldStatePreserveHint[],
): ApplyChangedDataPagePatch | null {
  const changedFields = collectMergedChangedFields(sanitizedChangedData)
  const changedParams = extractClientDataParamsFromChangedData(sanitizedChangedData)
  const forceReplaceTableFields = collectAuthoritativeTableReloadFieldNames(
    changedFields,
    changedParams,
  )
  const hasFieldChanges = Object.keys(changedFields).length > 0
  const hasParamChanges = Object.keys(changedParams).length > 0
  if (!hasFieldChanges && !hasParamChanges) {
    return null
  }

  let uniFields = hasFieldChanges
    ? mergePageFieldsRespectingStates(
        states,
        mergeFieldMapsFn,
        pageData.uniData?.fields?.fields || {},
        changedFields,
      )
    : pageData.uniData?.fields?.fields || {}
  if (hasFieldChanges && forceReplaceTableFields.length > 0) {
    uniFields = applyForceReplaceTableFields(uniFields, changedFields, forceReplaceTableFields)
  }

  let jsResultFields = hasFieldChanges
    ? mergePageFieldsRespectingStates(
        states,
        mergeFieldMapsFn,
        pageData.jsResult?.changed_data?.fields?.fields || {},
        changedFields,
      )
    : pageData.jsResult?.changed_data?.fields?.fields || {}
  if (hasFieldChanges && forceReplaceTableFields.length > 0) {
    jsResultFields = applyForceReplaceTableFields(
      jsResultFields,
      changedFields,
      forceReplaceTableFields,
    )
  }

  const clientDataParams = hasParamChanges
    ? {
        ...(pageData.uniData?.clientData?.params || {}),
        ...changedParams,
      }
    : undefined

  return {
    uniFields,
    jsResultFields,
    clientDataParams,
    forceReplaceTableFields,
  }
}

/** Применяет reloadElement / changed_data exchange к одному слоту pagesData. */
export function applyExchangeDataToAppStoreSlot(
  appStore: AppStore,
  slotId: string,
  exchangeData: unknown,
  setPageDataSource: string,
  getElements?: () => Record<string, any>,
  states?: FieldStatePreserveHint[],
): ApplyChangedDataPagePatch | null {
  publishExchangeFieldAttributes(exchangeData, slotId)
  const pageData = appStore.pagesData[slotId]
  if (!pageData) {
    return null
  }
  applyFormErrorFromPayload(slotId, exchangeData)
  const patch = buildChangedDataPagePatch(pageData, exchangeData, mergeFieldMaps, states)
  if (!patch) {
    return null
  }
  const changedFields = getRawChangedFields(exchangeData) || {}
  syncTableFieldsToElementRegistry(changedFields, getElements, patch.forceReplaceTableFields)

  appStore.setPageData(
    slotId,
    {
      ...pageData,
      uniData: {
        ...pageData.uniData,
        clientData: {
          ...pageData.uniData?.clientData,
          ...(patch.clientDataParams ? { params: patch.clientDataParams } : {}),
        },
        fields: {
          ...pageData.uniData?.fields,
          fields: patch.uniFields,
        },
      },
      jsResult: {
        ...pageData.jsResult,
        changed_data: {
          ...pageData.jsResult?.changed_data,
          fields: {
            ...pageData.jsResult?.changed_data?.fields,
            fields: patch.jsResultFields,
          },
        },
      },
      ...(patch.forceReplaceTableFields.length > 0
        ? { $forceReplaceTableFields: patch.forceReplaceTableFields }
        : {}),
    },
    undefined,
    undefined,
    setPageDataSource,
  )
  return patch
}

export function resetTreeTableSearchAfterCriteriaFieldClear(
  formId: string | number | undefined,
  clearedFieldName: string,
  dependencies?: ActionDependencies,
): void {
  if (formId == null) return
  const slotId = String(formId)
  const pageData = dependencies?.appStore?.pagesData?.[slotId] as
    | { uniData?: { fields?: { fields?: Record<string, unknown> } } }
    | undefined
  const pageFields = pageData ? mergePageDataFieldSnapshot(pageData) : {}
  const tableName = resolveTreeTableFieldNameFromPageFields(pageFields)
  if (
    !tableName ||
    !fieldParticipatesInTreeTableSearchCriteria(clearedFieldName, pageFields, tableName)
  ) {
    return
  }
  resetTreeTableSearchApplyState(formId)
  setTreeTableSearchSubmittedCriteria(formId, '\0')
}

/** Синхронный refill до merge changed_data */
export function applyPreemptiveRefillsFromExchangeStates(
  states: FieldStatePreserveHint[] | undefined,
  exchangeData: unknown,
  context: ActionContext,
  dependencies?: ActionDependencies,
): void {
  if (!states?.length || !dependencies?.formProvider?.refillElement) return
  const fields = collectMergedChangedFields(exchangeData)
  const refillElement = dependencies.formProvider.refillElement

  for (const state of states) {
    if (state.is_refill !== true && state.is_empty !== true) continue
    const elementName = String(state.element_name ?? '').trim()
    if (!elementName) continue

    const fieldData = fields[elementName]
    const isEmpty =
      fieldData != null
        ? fieldEntryLooksEmptyIncoming(fieldData)
        : state.is_empty === true || state.is_refill === true
    if (!isEmpty) continue

    const refillPayload =
      fieldData != null && typeof fieldData === 'object'
        ? fieldData
        : { type: 'String', value: state.is_empty ? null : '' }

    refillElement(elementName, refillPayload, context.formId)
    resetTreeTableSearchAfterCriteriaFieldClear(context.formId, elementName, dependencies)
  }
}
